import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import useSWR from "swr";
import styled from "styled-components/macro";
import { TertiaryButtonSmallV2 } from "../Buttons/Buttons";
import {
  ChatIcon,
  NewXIcon,
  ResetSessionIcon,
  WarningIcon,
} from "../Icons/Icons";
import { useStoreState } from "../../util/util";
import { useAuthContext } from "../Auth";
import { endpoints } from "../../endpoints";
import {
  ButtonSection,
  ChatBotContainer,
  ChatBotIcon,
  LogoContainer,
  TopSection,
} from "../ChatBot/ChatBot";
import { WarningMessageBox } from "../Form/Form";
import type { StorefrontChatbotConfiguration } from "../../types/types";
import { MessageTimeline } from "./MessageTimeline";
import { SearchContent, SearchFooter } from "./SearchLayout";
import { SearchComposer } from "./SearchComposer";
import { SuggestedPrompts } from "./SuggestedPrompts";
import type {
  AgenticSearchRunResponse,
  ChatMessage,
  ErrorKind,
  Feedback,
  SessionMessagesResponse,
} from "./types";

const AgenticSearchContainer = styled(ChatBotContainer)<{
  isOpen?: boolean;
  panelWidth: number;
  isResizing: boolean;
}>`
  top: 0;
  bottom: 0;
  right: 0;
  width: ${(props) => `${props.panelWidth}px`};
  height: 100vh;
  background: ${({ theme }) => theme.primaryBG};
  border-left: ${({ theme }) => theme.resolve("border_md")} solid
    ${(props) => props.theme.primaryBorder};
  border-radius: 0;
  box-shadow: 0px 1px 20px 0px ${({ theme }) => theme.shadowColor};
  transform: translate3d(
    ${(props) => (props.isOpen ? "0" : "calc(100% + 24px)")},
    0,
    0
  );
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.isOpen ? "auto" : "none")};
  transition: ${({ isResizing }) =>
    isResizing
      ? "transform 0.3s ease, opacity 0.2s ease"
      : "width 0.3s, transform 0.3s ease, opacity 0.2s ease"};
  @media (max-width: 1024px) {
    left: auto;
    right: 0;
  }
`;

const ResizeHandle = styled.button`
  position: absolute;
  left: -6px;
  top: 0;
  width: 18px;
  height: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: col-resize;
  z-index: 900;

  &::before {
    content: "";
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 5px;
    height: 46px;
    border-radius: ${({ theme }) => theme.resolve("radius_xxs")};
    background: ${({ theme }) => theme.primaryBorder};
    opacity: 0.9;
    transition: background-color 0.2s ease, opacity 0.2s ease;
  }

  &:hover::before,
  &:focus-visible::before {
    background: ${({ theme }) => theme.primaryTextColor};
    opacity: 1;
  }
`;

const AgenticSearchIcon = styled(ChatBotIcon)`
  transition: none;
  position: fixed;
  right: ${({ theme }) => theme.resolve("spacing_2xl")};
  bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
  margin: 0;
  z-index: 900;
`;

const AgenticButtonSection = styled(ButtonSection)`
  margin-left: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const HeaderName = styled.div`
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  color: ${({ theme }) => theme.primaryTextColor};
`;

const REQUEST_TIMEOUT_MS = 15_000;
const MAX_POLL_INTERVAL_MS = 3_000;
const AGENTIC_PANEL_WIDTH = {
  default: 480,
  min: 520,
  max: 900,
} as const;

function newId() {
  return Math.random().toString(36).slice(2);
}

// Map server notice types to translated bodies. Unknown types are dropped so
// new notice types can ship from the server without a coordinated client
// release breaking with raw `type` strings in the chat.
function renderNotice(type: string, t: (k: string) => string): string | null {
  if (type === "history_truncated") {
    return t(
      "This conversation is getting long. Click the reset button at the top to start a fresh chat — older messages have dropped out of Alchemist's memory."
    );
  }
  return null;
}

async function fetchChatbotConfig(
  url: string
): Promise<StorefrontChatbotConfiguration | null> {
  try {
    const response = await axios.get<StorefrontChatbotConfiguration>(url);
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (
        status === 403 ||
        status === 404 ||
        status === 502 ||
        status === 529
      ) {
        return null;
      }
    }
    throw error;
  }
}

function safeReadBool(key: string, fallback: boolean): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) === true;
  } catch {
    return fallback;
  }
}

function safeWrite(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage unavailable (private browsing, quota exceeded, etc.)
  }
}

function safeRead(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemove(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function createStopSignal() {
  let resolve!: (response: AgenticSearchRunResponse) => void;
  const promise = new Promise<AgenticSearchRunResponse>((done) => {
    resolve = done;
  });
  return { promise, resolve };
}

type PendingRun = {
  run_id: string;
  session_id: string;
  user_text: string;
  submitted_at: string;
  poll_after_ms?: number;
  stop_requested?: boolean;
};

function safeReadPendingRun(key: string): PendingRun | null {
  const raw = safeRead(key);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingRun>;
    if (
      typeof value.run_id !== "string" ||
      typeof value.session_id !== "string" ||
      typeof value.user_text !== "string" ||
      typeof value.submitted_at !== "string" ||
      (value.stop_requested !== undefined &&
        typeof value.stop_requested !== "boolean") ||
      (value.poll_after_ms !== undefined &&
        (typeof value.poll_after_ms !== "number" ||
          !Number.isFinite(value.poll_after_ms) ||
          value.poll_after_ms < 0))
    ) {
      return null;
    }
    return value as PendingRun;
  } catch {
    return null;
  }
}

export function pollDelay(baseMs: number, attempt: number): number {
  const requested = Math.max(100, baseMs || 1000);
  const backedOff = requested * (1 + Math.min(attempt, 10) * 0.15);
  // Add one-sided jitter so clients submitted in the same burst spread out,
  // without ever polling sooner than the server-requested interval.
  const jittered = backedOff + requested * Math.random() * 0.15;
  return Math.round(
    Math.max(requested, Math.min(MAX_POLL_INTERVAL_MS, jittered))
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

type AgenticErrorResponse = {
  message?: string;
  // Stable enum the backend stamps on chatbot failures. See
  // server/src/agilis/views/agentic_search/view.py#UPSTREAM_FAILURE_MAP.
  // Absent on auth / validation / not-found responses (those routes don't
  // go through the runner, so there's nothing to classify).
  error_code?: string;
};

type ErrorDetail = {
  status?: number;
  message?: string;
  backendMessage?: string;
  errorCode?: string;
  isTimeout: boolean;
};

// These are the backend's customer-facing messages for confirmed Gemini SDK
// failures. Match both code and full text so raw provider details cannot be
// displayed under a familiar upstream code.
const GEMINI_FAILURE_MESSAGES: Record<string, string> = {
  upstream_rate_limited:
    "Google Gemini returned a rate-limit error. Please try again shortly.",
  upstream_timeout:
    "Google Gemini did not respond in time. Please try again shortly.",
  upstream_unavailable:
    "We couldn't reach Google Gemini to complete your search. Please try again shortly.",
  upstream_server_error:
    "Google Gemini returned a server error, so we could not complete your search. Please try again shortly.",
};

function getErrorDetail(error: unknown): ErrorDetail {
  if (!axios.isAxiosError(error)) {
    return { isTimeout: false };
  }
  const responseData = error.response?.data as AgenticErrorResponse | undefined;
  const isTimeout =
    error.code === "ECONNABORTED" ||
    (typeof error.message === "string" && /timeout/i.test(error.message));
  return {
    status: error.response?.status,
    message: error.message,
    backendMessage: responseData?.message,
    errorCode: responseData?.error_code,
    isTimeout,
  };
}

function mapErrorMessage({
  detail,
  t,
  fallbackMessage,
  includeBackendMessage = true,
}: {
  detail: ErrorDetail;
  t: (key: string) => string;
  fallbackMessage: string;
  includeBackendMessage?: boolean;
}): { message: string; kind: ErrorKind } {
  const { status, isTimeout, backendMessage, message, errorCode } = detail;
  const geminiMessage = errorCode && GEMINI_FAILURE_MESSAGES[errorCode];
  if (geminiMessage && backendMessage === geminiMessage) {
    return { message: t(geminiMessage), kind: "upstream" };
  }
  // Branch on backend's stable error_code first — survives rephrasing of
  // the user-facing message and lets us pick the right severity styling.
  if (errorCode === "upstream_rate_limited") {
    return {
      message: t("The model provider is rate-limited. Try again in a moment."),
      kind: "upstream",
    };
  }
  if (errorCode === "upstream_timeout") {
    return {
      message: t("The model provider took too long. Try a simpler question."),
      kind: "upstream",
    };
  }
  if (errorCode === "upstream_unavailable") {
    return {
      message: t(
        "We can't reach the model provider right now. Try again shortly."
      ),
      kind: "upstream",
    };
  }
  if (errorCode === "upstream_server_error") {
    return {
      message: t("The model provider is having trouble. Try again shortly."),
      kind: "upstream",
    };
  }
  if (errorCode === "upstream_overloaded") {
    return {
      message: t(
        "The model provider is overloaded. Try again in a few minutes."
      ),
      kind: "upstream",
    };
  }
  if (errorCode === "turn_timeout") {
    return {
      message: t(
        "Agentic Search reached its time limit. Try a simpler question."
      ),
      kind: "upstream",
    };
  }
  if (errorCode === "internal_error") {
    return {
      message: t("Agentic search failed. The error has been logged."),
      kind: "fatal",
    };
  }
  // Status-based fallbacks for proxy/transport layers that don't carry an
  // error_code (nginx 502, raw 529 from a service we don't proxy through).
  if (status === 502) {
    return {
      message: t("Your request could not be completed, please try again"),
      kind: "upstream",
    };
  }
  if (status === 529) {
    return {
      message: t(
        "The service is temporarily unavailable. Please try again in a few minutes"
      ),
      kind: "upstream",
    };
  }
  if (status === 429) {
    return {
      message: t(
        "You're going faster than Alchemist can answer. Try again in a moment."
      ),
      kind: "upstream",
    };
  }
  if (status === 401 || status === 403) {
    return {
      message: t(
        "You don't have access to Alchemist. Refresh to confirm you're still signed in."
      ),
      kind: "fatal",
    };
  }
  if (isTimeout) {
    return {
      message: t("That took too long. Try a simpler question."),
      kind: "upstream",
    };
  }
  if (includeBackendMessage) {
    return {
      message: backendMessage || message || fallbackMessage,
      kind: "fatal",
    };
  }
  return { message: fallbackMessage, kind: "fatal" };
}

function clampPanelWidth(width: number): number {
  if (Number.isNaN(width)) return AGENTIC_PANEL_WIDTH.default;
  const viewportMax =
    typeof window !== "undefined"
      ? window.innerWidth - 10
      : AGENTIC_PANEL_WIDTH.max;
  const maxAllowed = Math.min(AGENTIC_PANEL_WIDTH.max, viewportMax);
  return Math.min(maxAllowed, Math.max(AGENTIC_PANEL_WIDTH.min, width));
}

export function AgenticSearchWidget() {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const { tenant_id, storefront_id } = useStoreState();
  const { roleIsSomeKindOfSeller } = useAuthContext();

  // The chatbot config endpoint 403s when chat isn't configured for this
  // storefront. The agentic widget can mount on storefronts where only
  // `enable_agentic_search` is true, so skip the fetch unless chat config
  // actually exists — otherwise the global SwrOnError handler treats the
  // 403 as "log out" and bounces the user to /login.
  const { data: config } = useSWR<StorefrontChatbotConfiguration | null>(
    storefront_id ? `/v2/storefronts/${storefront_id}/chatbots` : null,
    fetchChatbotConfig,
    {
      revalidateOnFocus: false,
    }
  );

  const [isOpen, setIsOpen] = useState(() =>
    safeReadBool("agenticSearchIsOpen", false)
  );
  const panelWidth = useMemo(() => {
    const raw = safeRead("agenticSearchPanelWidth");
    if (!raw) return AGENTIC_PANEL_WIDTH.default;
    const parsed = Number(raw);
    return clampPanelWidth(parsed);
  }, []);
  const [currentPanelWidth, setCurrentPanelWidth] =
    useState<number>(panelWidth);
  const currentPanelWidthRef = useRef<number>(panelWidth);

  const sessionStorageKey = `agenticSearch_session_${tenant_id || "anon"}`;
  const pendingRunStorageKey = `agenticSearch_run_${tenant_id || "anon"}`;
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const stopRequestedRef = useRef(false);
  const stopSignalRef =
    useRef<ReturnType<typeof createStopSignal> | null>(null);
  const stopResponseRef = useRef<AgenticSearchRunResponse | null>(null);
  const [sessionRestoreError, setSessionRestoreError] =
    useState<string | null>(null);
  // Notice types we already rendered into the timeline. Server can re-emit a
  // notice on every turn (e.g. `history_truncated` keeps firing once the cap
  // is hit); we render it once per session so the chat doesn't fill up with
  // duplicates.
  const renderedNoticesRef = useRef<Set<string>>(new Set());
  const contentRef = useRef<HTMLDivElement>(null);
  const cancelSourceRef =
    useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const activeRunRef = useRef<PendingRun | null>(null);
  const isResizingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty(
        "--agentic-search-panel-width",
        "0px"
      );
      cancelSourceRef.current?.cancel("widget unmounted");
      cancelSourceRef.current = null;
    };
  }, []);

  useEffect(() => {
    const width = isOpen ? currentPanelWidth : 0;
    document.documentElement.style.setProperty(
      "--agentic-search-panel-width",
      `${width}px`
    );
  }, [isOpen, currentPanelWidth]);

  useEffect(() => {
    currentPanelWidthRef.current = currentPanelWidth;
  }, [currentPanelWidth]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current) return;
      const nextWidth = clampPanelWidth(window.innerWidth - event.clientX);
      currentPanelWidthRef.current = nextWidth;
      setCurrentPanelWidth(nextWidth);
    };

    const onMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
      safeWrite(
        "agenticSearchPanelWidth",
        String(currentPanelWidthRef.current)
      );
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!tenant_id) return;
    const stored = safeRead(sessionStorageKey);
    if (!stored) return;
    const recovering = safeReadPendingRun(pendingRunStorageKey);

    let cancelled = false;
    const source = axios.CancelToken.source();
    axios
      .get<SessionMessagesResponse>(
        endpoints.v2_tenants_id_agenticSearch_session_messages(
          tenant_id,
          stored
        ),
        { cancelToken: source.token }
      )
      .then((response) => {
        if (cancelled) return;
        const restored: ChatMessage[] = response.data.messages.map((m) => {
          const mappedError =
            m.role === "error"
              ? mapErrorMessage({
                  detail: {
                    errorCode: m.error?.code,
                    backendMessage: m.error?.message,
                    isTimeout: false,
                  },
                  t: tRef.current,
                  fallbackMessage: tRef.current(
                    "Agentic search failed. The error has been logged."
                  ),
                  includeBackendMessage: false,
                })
              : null;
          return {
            id: m.id,
            role: m.role,
            content: mappedError?.message ?? m.content ?? "",
            citations: m.citations,
            tool_calls: m.tool_calls,
            tables: m.tables,
            created_at: m.created_at,
            server_message_id: m.id,
            feedback: m.feedback ?? null,
            error_kind: mappedError?.kind,
          };
        });
        setMessages((prev) => {
          if (prev.length === 0) return restored;
          if (!recovering || recovering.session_id !== stored) return prev;

          const recoveredUserId = `user-${recovering.run_id}`;
          const recoveredAnswerId = `pending-${recovering.run_id}`;
          const savedUserIndex = restored
            .map((message) =>
              message.role === "user" ? message.content : null
            )
            .lastIndexOf(recovering.user_text);
          const savedAnswerExists =
            savedUserIndex >= 0 &&
            restored
              .slice(savedUserIndex + 1)
              .some(
                (message) =>
                  message.role === "assistant" ||
                  message.role === "error" ||
                  (message.role === "info" && message.id === recovering.run_id)
              );
          const restoredIds = new Set(restored.map((message) => message.id));
          return [
            ...restored,
            ...prev.filter(
              (message) =>
                !restoredIds.has(message.id) &&
                !(message.id === recoveredUserId && savedUserIndex >= 0) &&
                !(message.id === recoveredAnswerId && savedAnswerExists)
            ),
          ];
        });
        setSessionId(stored);
        setSessionRestoreError(null);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        const detail = getErrorDetail(err);
        // Session no longer exists or user lost access — drop the stale id.
        safeRemove(sessionStorageKey);
        setSessionRestoreError(
          mapErrorMessage({
            detail,
            t: tRef.current,
            fallbackMessage: tRef.current(
              "We couldn't restore your previous chat. Start a new question."
            ),
            includeBackendMessage: false,
          }).message
        );
      });

    return () => {
      cancelled = true;
      source.cancel("hydration superseded");
    };
  }, [tenant_id, sessionStorageKey, pendingRunStorageKey]);

  const toggleOpen = () => {
    safeWrite("agenticSearchIsOpen", (!isOpen).toString());
    setIsOpen(!isOpen);
  };

  const handleReset = useCallback(() => {
    const active =
      activeRunRef.current || safeReadPendingRun(pendingRunStorageKey);
    if (tenant_id && active) {
      axios
        .delete(
          endpoints.v2_tenants_id_agenticSearch_run(tenant_id, active.run_id)
        )
        .catch(() => undefined);
    }
    cancelSourceRef.current?.cancel("session reset");
    cancelSourceRef.current = null;
    activeRunRef.current = null;
    safeRemove(sessionStorageKey);
    safeRemove(pendingRunStorageKey);
    setInProgress(false);
    setStopping(false);
    setStopError(null);
    setConnectionError(null);
    stopRequestedRef.current = false;
    stopResponseRef.current = null;
    stopSignalRef.current = null;
    setSessionId(null);
    setMessages([]);
    setSessionRestoreError(null);
    renderedNoticesRef.current.clear();
  }, [pendingRunStorageKey, sessionStorageKey, tenant_id]);

  const applyTerminalRun = useCallback(
    (data: AgenticSearchRunResponse, pendingId: string) => {
      if (data.status === "cancelled") {
        setMessages((prev) =>
          prev.some((message) => message.id === data.run_id)
            ? prev.filter((message) => message.id !== pendingId)
            : prev.map((message) =>
                message.id === pendingId
                  ? {
                      id: data.run_id,
                      role: "info" as const,
                      content: tRef.current("Search stopped."),
                      created_at: data.completed_at || new Date().toISOString(),
                    }
                  : message
              )
        );
        return;
      }
      if (data.status === "failed") {
        const mapped = mapErrorMessage({
          detail: {
            backendMessage: data.error?.message,
            errorCode: data.error?.code,
            isTimeout: data.error?.code === "turn_timeout",
          },
          t: tRef.current,
          fallbackMessage: tRef.current(
            "There was an error sending your message"
          ),
        });
        setMessages((prev) =>
          data.assistant_message_id &&
          prev.some(
            (message) => message.server_message_id === data.assistant_message_id
          )
            ? prev.filter((message) => message.id !== pendingId)
            : prev.map((message) =>
                message.id === pendingId
                  ? {
                      id: pendingId,
                      role: "error" as const,
                      content: mapped.message,
                      error_kind: mapped.kind,
                      created_at: new Date().toISOString(),
                    }
                  : message
              )
        );
        return;
      }

      const answer = (data.answer ?? "").trim()
        ? data.answer || ""
        : tRef.current("(empty answer)");
      const noticeMessages: ChatMessage[] = [];
      for (const notice of data.notices || []) {
        if (renderedNoticesRef.current.has(notice.type)) continue;
        renderedNoticesRef.current.add(notice.type);
        const body = renderNotice(notice.type, tRef.current);
        if (!body) continue;
        noticeMessages.push({
          id: newId(),
          role: "info",
          content: body,
          created_at: new Date().toISOString(),
        });
      }
      setMessages((prev) =>
        data.assistant_message_id &&
        prev.some(
          (message) => message.server_message_id === data.assistant_message_id
        )
          ? prev.filter((message) => message.id !== pendingId)
          : prev.flatMap((message) =>
              message.id === pendingId
                ? [
                    {
                      ...message,
                      content: answer,
                      citations: data.citations,
                      tool_calls: data.tool_calls,
                      tables: data.tables,
                      usage: data.usage || undefined,
                      pending: false,
                      created_at: data.completed_at || new Date().toISOString(),
                      server_message_id: data.assistant_message_id || undefined,
                      feedback: null,
                    },
                    ...noticeMessages,
                  ]
                : [message]
            )
      );
    },
    []
  );

  const requestStop = useCallback(
    async (source: ReturnType<typeof axios.CancelToken.source>) => {
      const active = activeRunRef.current;
      if (!tenant_id || !active) return;
      try {
        const response = await axios.delete<AgenticSearchRunResponse>(
          endpoints.v2_tenants_id_agenticSearch_run(tenant_id, active.run_id),
          { timeout: REQUEST_TIMEOUT_MS, cancelToken: source.token }
        );
        if (cancelSourceRef.current !== source) return;
        stopResponseRef.current = response.data;
        if (
          ["cancelled", "completed", "failed"].includes(response.data.status)
        ) {
          stopSignalRef.current?.resolve(response.data);
        }
      } catch (error: unknown) {
        if (axios.isCancel(error) || cancelSourceRef.current !== source) return;
        stopRequestedRef.current = false;
        if (activeRunRef.current) {
          activeRunRef.current = {
            ...activeRunRef.current,
            stop_requested: false,
          };
          safeWrite(pendingRunStorageKey, JSON.stringify(activeRunRef.current));
        }
        setStopping(false);
        setMessages((prev) =>
          prev.map((message) =>
            message.pending ? { ...message, stopping: false } : message
          )
        );
        setStopError(
          tRef.current(
            "We couldn't confirm that the search stopped. Please try Stop again."
          )
        );
      }
    },
    [tenant_id, pendingRunStorageKey]
  );

  const handleStop = useCallback(() => {
    const source = cancelSourceRef.current;
    if (!source || stopRequestedRef.current) return;
    stopRequestedRef.current = true;
    setStopping(true);
    setStopError(null);
    setMessages((prev) =>
      prev.map((message) =>
        message.pending ? { ...message, stopping: true } : message
      )
    );
    // Before admission finishes, retain intent and DELETE as soon as the ID arrives.
    if (activeRunRef.current) {
      activeRunRef.current = { ...activeRunRef.current, stop_requested: true };
      safeWrite(pendingRunStorageKey, JSON.stringify(activeRunRef.current));
      void requestStop(source);
    }
  }, [requestStop, pendingRunStorageKey]);

  const pollRun = useCallback(
    async (
      initial: AgenticSearchRunResponse,
      source: ReturnType<typeof axios.CancelToken.source>,
      pendingId: string
    ): Promise<AgenticSearchRunResponse> => {
      if (!tenant_id) return initial;
      const stopSignal = stopSignalRef.current?.promise;
      let current = initial;
      let attempt = 0;
      let consecutiveErrors = 0;
      while (
        current.status === "queued" ||
        current.status === "running" ||
        current.status === "cancel_requested"
      ) {
        if (current.status === "cancel_requested") {
          stopRequestedRef.current = true;
          setStopping(true);
          setStopError(null);
        }
        const snapshot = current;
        setMessages((prev) =>
          prev.map((message) =>
            message.id === pendingId
              ? {
                  ...message,
                  stopping: stopRequestedRef.current,
                  progress:
                    !stopRequestedRef.current &&
                    snapshot.progress &&
                    snapshot.progress.sequence >=
                      (message.progress?.sequence ?? 0)
                      ? snapshot.progress
                      : message.progress,
                }
              : message
          )
        );
        const delay = wait(pollDelay(current.poll_after_ms, attempt));
        await (stopSignal ? Promise.race([delay, stopSignal]) : delay);
        source.token.throwIfRequested();
        if (
          stopResponseRef.current &&
          ["cancelled", "completed", "failed"].includes(
            stopResponseRef.current.status
          )
        ) {
          return stopResponseRef.current;
        }
        try {
          const poll = axios.get<AgenticSearchRunResponse>(
            endpoints.v2_tenants_id_agenticSearch_run(
              tenant_id,
              current.run_id
            ),
            { cancelToken: source.token, timeout: REQUEST_TIMEOUT_MS }
          );
          const response = await (stopSignal
            ? Promise.race([poll, stopSignal.then((data) => ({ data }))])
            : poll);
          source.token.throwIfRequested();
          current =
            stopResponseRef.current &&
            ["cancelled", "completed", "failed"].includes(
              stopResponseRef.current.status
            )
              ? stopResponseRef.current
              : response.data;
          setConnectionError(null);
          consecutiveErrors = 0;
          attempt += 1;
        } catch (error: unknown) {
          if (axios.isCancel(error)) throw error;
          const status = getErrorDetail(error).status;
          if (status === 401 || status === 403 || status === 404) throw error;
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            setConnectionError(
              tRef.current(
                "Connection interrupted. Reconnecting to your search…"
              )
            );
          }
          attempt += 1;
        }
      }
      return current;
    },
    [tenant_id]
  );

  useEffect(() => {
    if (!tenant_id) return undefined;
    const pending = safeReadPendingRun(pendingRunStorageKey);
    if (!pending) return undefined;

    const source = axios.CancelToken.source();
    const pendingId = `pending-${pending.run_id}`;
    activeRunRef.current = pending;
    cancelSourceRef.current = source;
    stopRequestedRef.current = !!pending.stop_requested;
    stopResponseRef.current = null;
    stopSignalRef.current = createStopSignal();
    setStopping(!!pending.stop_requested);
    setInProgress(true);
    setSessionId(pending.session_id);
    setMessages((prev) => {
      if (prev.some((message) => message.id === pendingId)) return prev;
      return [
        ...prev,
        {
          id: `user-${pending.run_id}`,
          role: "user",
          content: pending.user_text,
          created_at: pending.submitted_at,
        },
        {
          id: pendingId,
          role: "assistant",
          content: "",
          created_at: pending.submitted_at,
          pending: true,
        },
      ];
    });

    const initial: AgenticSearchRunResponse = {
      run_id: pending.run_id,
      session_id: pending.session_id,
      status: "queued",
      // Old stored runs predate the hint field; retain a conservative fallback.
      poll_after_ms: pending.poll_after_ms ?? 1000,
      queued_at: pending.submitted_at,
      started_at: null,
      completed_at: null,
      answer: null,
      assistant_message_id: null,
      citations: [],
      tool_calls: [],
      usage: null,
      notices: [],
      error: null,
    };

    if (pending.stop_requested) void requestStop(source);
    pollRun(initial, source, pendingId)
      .then((terminal) => {
        source.token.throwIfRequested();
        applyTerminalRun(terminal, pendingId);
        safeRemove(pendingRunStorageKey);
        activeRunRef.current = null;
      })
      .catch((error: unknown) => {
        if (axios.isCancel(error)) return;
        const mapped = mapErrorMessage({
          detail: getErrorDetail(error),
          t: tRef.current,
          fallbackMessage: tRef.current(
            "We lost contact with the request. Reload to keep checking it."
          ),
          includeBackendMessage: false,
        });
        setMessages((prev) =>
          prev.map((message) =>
            message.id === pendingId
              ? {
                  id: pendingId,
                  role: "error" as const,
                  content: mapped.message,
                  error_kind: mapped.kind,
                  created_at: new Date().toISOString(),
                }
              : message
          )
        );
      })
      .finally(() => {
        if (cancelSourceRef.current === source) {
          cancelSourceRef.current = null;
          stopRequestedRef.current = false;
          stopResponseRef.current = null;
          stopSignalRef.current = null;
          setStopping(false);
          setStopError(null);
          setConnectionError(null);
          setInProgress(false);
        }
      });

    return () => source.cancel("pending run recovery superseded");
  }, [applyTerminalRun, pendingRunStorageKey, pollRun, tenant_id, requestStop]);

  const submitMessage = useCallback(
    async (text: string) => {
      if (!tenant_id || inProgress) return;
      cancelSourceRef.current?.cancel("superseded");
      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      const pendingId = newId();
      stopRequestedRef.current = false;
      stopResponseRef.current = null;
      stopSignalRef.current = createStopSignal();
      activeRunRef.current = null;
      setStopping(false);
      setStopError(null);
      setConnectionError(null);
      setInProgress(true);
      setSessionRestoreError(null);
      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: "user",
          content: text,
          created_at: new Date().toISOString(),
        },
        {
          id: pendingId,
          role: "assistant",
          content: "",
          created_at: new Date().toISOString(),
          pending: true,
        },
      ]);

      try {
        const url = endpoints.v2_tenants_id_agenticSearch_runs(tenant_id);
        const body: { message: string; session_id?: string } = {
          message: text,
        };
        if (sessionId) body.session_id = sessionId;
        const response = await axios.post<AgenticSearchRunResponse>(url, body, {
          cancelToken: source.token,
          timeout: REQUEST_TIMEOUT_MS,
        });
        const pendingRun: PendingRun = {
          run_id: response.data.run_id,
          session_id: response.data.session_id,
          user_text: text,
          submitted_at: new Date().toISOString(),
          poll_after_ms: response.data.poll_after_ms,
          stop_requested: stopRequestedRef.current,
        };
        activeRunRef.current = pendingRun;
        safeWrite(pendingRunStorageKey, JSON.stringify(pendingRun));
        setSessionId(response.data.session_id);
        safeWrite(sessionStorageKey, response.data.session_id);

        if (stopRequestedRef.current) void requestStop(source);
        const terminal = await pollRun(response.data, source, pendingId);
        source.token.throwIfRequested();
        applyTerminalRun(terminal, pendingId);
        activeRunRef.current = null;
        safeRemove(pendingRunStorageKey);
      } catch (err: unknown) {
        if (axios.isCancel(err)) {
          setMessages((prev) => prev.filter((m) => m.id !== pendingId));
          return;
        }
        const mapped = mapErrorMessage({
          detail: getErrorDetail(err),
          t,
          fallbackMessage: t("There was an error sending your message"),
        });
        setMessages((prev) =>
          prev.flatMap((m) =>
            m.id === pendingId
              ? [
                  {
                    id: pendingId,
                    role: "error" as const,
                    content: mapped.message,
                    error_kind: mapped.kind,
                    created_at: new Date().toISOString(),
                  },
                ]
              : [m]
          )
        );
      } finally {
        if (cancelSourceRef.current === source) {
          cancelSourceRef.current = null;
          stopRequestedRef.current = false;
          stopResponseRef.current = null;
          stopSignalRef.current = null;
          setStopping(false);
          setStopError(null);
          setConnectionError(null);
          setInProgress(false);
        }
      }
    },
    [
      tenant_id,
      sessionId,
      t,
      sessionStorageKey,
      pendingRunStorageKey,
      inProgress,
      pollRun,
      applyTerminalRun,
      requestStop,
    ]
  );

  const handlePickPrompt = useCallback(
    (prompt: string) => {
      submitMessage(prompt);
    },
    [submitMessage]
  );

  const handleSetFeedback = useCallback(
    async (clientMessageId: string, next: Feedback | null) => {
      if (!tenant_id) return;
      const target = messages.find((m) => m.id === clientMessageId);
      const serverId = target?.server_message_id;
      if (!serverId) return;
      const previous = target?.feedback ?? null;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === clientMessageId ? { ...m, feedback: next } : m
        )
      );
      try {
        const url = endpoints.v2_tenants_id_agenticSearch_message_feedback(
          tenant_id,
          serverId
        );
        if (next === null) {
          await axios.delete(url);
        } else {
          await axios.put(url, {
            rating: next.rating,
            comment: next.comment ?? null,
          });
        }
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === clientMessageId ? { ...m, feedback: previous } : m
          )
        );
      }
    },
    [tenant_id, messages]
  );

  if (!tenant_id) return null;
  if (!roleIsSomeKindOfSeller) return null;

  const introMessage = config?.intro_message || t("Hello, How may I help you?");

  return (
    <>
      {!isOpen && (
        <AgenticSearchIcon isOpen={false} onClick={toggleOpen}>
          <ChatIcon />
          <span style={{ marginLeft: "10px" }}>
            {t("Chat with an Assistant")}
          </span>
        </AgenticSearchIcon>
      )}
      <AgenticSearchContainer
        isOpen={isOpen}
        panelWidth={currentPanelWidth}
        isResizing={isResizing}
      >
        <ResizeHandle
          type="button"
          aria-label={t("Resize")}
          onMouseDown={(event) => {
            event.preventDefault();
            document.body.style.setProperty("cursor", "col-resize");
            document.body.style.setProperty("user-select", "none");
            isResizingRef.current = true;
            setIsResizing(true);
          }}
        />
        <TopSection>
          <LogoContainer>
            {config?.avatar && (
              <img
                src={config.avatar}
                alt={t("Virtual Chemist")}
                style={{
                  maxWidth: "70px",
                  maxHeight: "50px",
                  margin: "0 10px 0 0",
                }}
              />
            )}
            <HeaderName>{config?.name || t("Virtual Chemist")}</HeaderName>
          </LogoContainer>
          <AgenticButtonSection>
            <TertiaryButtonSmallV2
              onClick={handleReset}
              disabled={!sessionId && messages.length === 0 && !inProgress}
              type="button"
              title={t("Reset session")}
              aria-label={t("Reset session")}
              style={{ width: 30, height: 30, minWidth: 30, padding: 0 }}
            >
              <ResetSessionIcon width={16} height={16} />
            </TertiaryButtonSmallV2>
            <TertiaryButtonSmallV2
              onClick={toggleOpen}
              type="button"
              title={t("Close")}
              aria-label={t("Close")}
              style={{ width: 30, height: 30, minWidth: 30, padding: 0 }}
            >
              <NewXIcon />
            </TertiaryButtonSmallV2>
          </AgenticButtonSection>
        </TopSection>

        <SearchContent isOpen={true} isExpanded={false} ref={contentRef}>
          {sessionRestoreError && (
            <WarningMessageBox role="status" aria-live="polite">
              <WarningIcon width={16} height={16} />
              {sessionRestoreError}
            </WarningMessageBox>
          )}
          {messages.length === 0 ? (
            <SuggestedPrompts
              onPick={handlePickPrompt}
              intro={introMessage}
              groups={config?.suggested_prompts}
            />
          ) : (
            <MessageTimeline
              tenantId={tenant_id}
              chatBotName={config?.name}
              messages={messages}
              contentRef={contentRef}
              onSetFeedback={handleSetFeedback}
            />
          )}
        </SearchContent>
        <SearchFooter isOpen={true} isExpanded={false}>
          {stopError && <div role="alert">{stopError}</div>}
          {connectionError && <div role="status">{connectionError}</div>}
          <SearchComposer
            disclaimer={config?.inside_login_disclaimer}
            onSubmit={submitMessage}
            inProgress={inProgress}
            onStop={handleStop}
            stopping={stopping}
          />
        </SearchFooter>
      </AgenticSearchContainer>
    </>
  );
}
